import React, { useEffect, useState } from 'react';
import { Save, AlertTriangle, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { communicationsService, fileService } from '../../../services/auditService';
import apiClient from '../../../services/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../../validation/ValidatedFields';

/**
 * CommunicationModal — raise an internal or external communication (query thread)
 * directly from a procedure's execution view, pre-linked to that procedure
 * instance. Mounted by ResponseShell alongside the per-row Raise-finding (memo)
 * action so every procedure exposes all three audit interactions:
 *   - Memo / finding         (per response row → ObservationModal)
 *   - Internal communication (this modal, channel='internal' — agency audit team)
 *   - External communication (this modal, channel='external' — to the GRC team)
 *
 * Both channels support a single file attachment (data exchange with GRC on
 * external threads; supporting evidence on internal threads). The created thread
 * is visible in the Queries (internal) / Communication (external) module.
 * Backend: POST /api/v1/adt/communications.
 */

export interface CommunicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  channel: 'internal' | 'external';
  procedureInstanceId: number;
  procedureName: string;
  programInstanceId?: number | null;
  onCreated?: () => void;
}

const COPY: Record<'internal' | 'external', { title: string }> = {
  internal: {
    title: 'Internal Communication',
  },
  external: {
    title: 'External Communication',
  },
};

const CommunicationModal: React.FC<CommunicationModalProps> = ({
  isOpen,
  onClose,
  channel,
  procedureInstanceId,
  procedureName,
  programInstanceId,
  onCreated,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const userId = state.user?.id ?? 0;

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grcUsers, setGrcUsers] = useState<{ id: number; full_name: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);
  const copy = COPY[channel];
  const vh = useFieldValidation();

  useEffect(() => {
    if (!isOpen) return;
    setSubject('');
    setMessage('');
    setFile(null);
    setShowUpload(false);
    setAttachOpen(false);
    setSubmitting(false);
    setError(null);
    setRecipientIds([]);
    setGrcUsers([]);
    setLoadingUsers(false);
    vh.reset();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load GRC users when modal opens in external mode.
  useEffect(() => {
    if (!isOpen || channel !== 'external') return;
    let cancelled = false;
    setLoadingUsers(true);
    // Recipient picker — use the un-gated, entity-scoped dropdown endpoint so a
    // staff auditor (who lacks Users-RBAC "view") can still load recipients.
    apiClient.get('/api/v1/usr/users/dropdown/all').then((resp: any) => {
      if (cancelled) return;
      const raw = resp?.data?.data ?? resp?.data ?? [];
      setGrcUsers((Array.isArray(raw) ? raw : []).map((u: any) => ({
        id: u.id,
        full_name: u.name?.trim() || u.email || `User #${u.id}`,
      })));
      setLoadingUsers(false);
    }).catch(() => { if (!cancelled) { setGrcUsers([]); setLoadingUsers(false); } });
    return () => { cancelled = true; };
  }, [isOpen, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = !submitting && subject.trim().length >= 3 && message.trim().length >= 1
    && (channel !== 'external' || recipientIds.length > 0);

  const handleSubmit = async () => {
    const allValid = vh.validateAll([
      { name: 'subject', code: 110, value: subject, required: true },
      { name: 'message', code: 110, value: message, required: true },
    ]);
    if (!allValid) return;
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      let attachmentFileId: number | undefined;
      if (file) {
        try {
          const up = await fileService.upload(file, entityId, userId);
          attachmentFileId = up.id;
        } catch (uploadErr: any) {
          setError(uploadErr?.response?.data?.detail ?? uploadErr?.message ?? 'Attachment upload failed.');
          setSubmitting(false);
          return;
        }
      }
      await communicationsService.create({
        subject: subject.trim(),
        channel,
        first_message: message.trim(),
        procedure_instance_id: procedureInstanceId,
        ...(programInstanceId ? { program_instance_id: programInstanceId } : {}),
        ...(attachmentFileId ? { attachment_file_id: attachmentFileId } : {}),
        recipient_user_ids: channel === 'external' ? recipientIds : undefined,
      });
      onCreated?.();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to raise communication.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape data-testid="MOD_communication_root">
      <Modal.Header>{copy.title}</Modal.Header>

      <Modal.Body>
        <div className="observation-context-card">
          <div className="observation-context-title">{procedureName}</div>
        </div>

        <form
          id="communication-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="flex flex-col gap-y-4"
          style={{ marginTop: 12 }}
        >
          <ValidatedInput
            vh={vh}
            name="subject"
            code={110}
            required
            type="text"
            label="Subject"
            value={subject}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
            data-testid="INP_communication_subject"
          />
          <ValidatedTextarea
            vh={vh}
            name="message"
            code={110}
            required
            label="Opening Message"
            rows={4}
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
            data-testid="INP_communication_message"
          />
          {channel === 'external' && (
            <div data-testid="FLD_communication_recipients">
              {loadingUsers ? (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                    Recipients <span style={{ color: 'var(--danger, #dc2626)' }}>*</span>
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading users…</span>
                </>
              ) : grcUsers.length === 0 ? (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                    Recipients <span style={{ color: 'var(--danger, #dc2626)' }}>*</span>
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>
                    Failed to load users. Please close and reopen.
                  </span>
                </>
              ) : (
                <FormControl.MultiSelect
                  label="Recipients"
                  required
                  placeholder="Select recipients…"
                  options={grcUsers.map(u => ({ value: u.id, label: u.full_name }))}
                  value={recipientIds}
                  onChange={(vals) => setRecipientIds(vals.map(Number))}
                  error={recipientIds.length === 0 ? 'At least one recipient is required.' : undefined}
                />
              )}
            </div>
          )}

          <div>
            {/* Attachment is optional — keep the dropzone hidden until the user
                opts in via the paperclip toggle (default collapsed). */}
            {!showUpload && !file ? (
              <button
                type="button"
                className="comm-attach-toggle"
                onClick={() => setShowUpload(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 13, color: 'var(--interactive-primary-bg)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
                data-testid="BTN_communication_attach_toggle"
              >
                <Paperclip size={14} /> Attach file
              </button>
            ) : (
              <>
                <FormControl.FileUpload
                  disabled={submitting}
                  onChange={(files: FileList | null) => setFile(files && files[0] ? files[0] : null)}
                />
                {file && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Paperclip size={12} /> {file.name}
                  </div>
                )}
              </>
            )}
          </div>
        </form>

        {error && (
          <div className="inline-error inline-error--inline" role="alert" style={{ marginTop: 10 }}>
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="submit"
          form="communication-form"
          variant="fill"
          icon={Save}
          disabled={!canSubmit}
          data-testid="BTN_communication_submit"
        >
          Raise
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommunicationModal;
